import {noop} from '@benchling/js-utils';
import {createStyles} from '@mantine/core';
import React, {useMemo} from 'react';

import {QueryResultType} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {FilledScratchPlateData} from 'notebook/integrations/plate-maps/scratch-plates/FilledScratchPlateUtil';
import {useWellSchemaOptionsForPlateMap} from 'notebook/integrations/plate-maps/scratch-plates/ScratchPlateDesignSummaryUtil';
import useFilledScratchPlateData, {
  UseFilledScratchPlateDataResult,
} from 'notebook/integrations/plate-maps/scratch-plates/useFilledScratchPlateData';
import {PlateMapType} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import PlateMapDisplayLayerDropdown from 'samples/plate-maps/display-layer-controls/PlateMapDisplayLayerDropdown';
import usePlateMapDisplayAttributeState from 'samples/plate-maps/hooks/usePlateMapDisplayAttributeState';
import useWellSchemaLayers from 'samples/plate-maps/hooks/useWellSchemaLayers';
import {
  ReagentAssignmentGroup,
  REAGENTS_ATTRIBUTE_SENTINEL,
  SupportedAdditionalAttribute,
  WELL_ROLE_ATTRIBUTE_SENTINEL,
} from 'samples/plate-maps/PlateMapConstants';
import PlateMapPane from 'samples/plate-maps/PlateMapPane';
import {getContentConcentrationOptions, getDisplayLayerAttributeKey} from 'samples/plate-maps/PlateMapUtil';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import type {RunModalProps} from 'ui-library/runModal';
import {CenteredSpinner} from 'ui-library/Spinner';
import {assertNever} from 'util/Types';

interface QueryingScratchPlateViewerProps {
  scratchPlateId: string;
}

const QueryingScratchPlateViewer: React.FC<QueryingScratchPlateViewerProps> = ({scratchPlateId}) => {
  const result = useFilledScratchPlateData(scratchPlateId);
  return <ScratchPlateViewer result={result} />;
};

const BannerWrapper: React.FC<{children: React.ReactNode}> = ({children}) => {
  const {classes} = useStyles();
  return <div className={classes.bannerWrapper}>{children}</div>;
};

const LoadingSpinner: React.FC = () => {
  const {classes} = useStyles();
  return (
    <div className={classes.loadingSpinner}>
      <CenteredSpinner />
    </div>
  );
};

export enum QueryBannerState {
  LOADING = 'loading',
  ERROR = 'error',
  INACCESSIBLE_PLATE = 'inaccessible-plate',
}

export const QUERY_BANNER_CONTENT: Record<
  QueryBannerState,
  {
    bannerType: InlineBannerType;
    text: string;
    supportingInfo?: React.ReactNode;
  }
> = {
  [QueryBannerState.LOADING]: {
    bannerType: InlineBannerType.INFO,
    text: 'Loading plate contents…',
  },
  [QueryBannerState.INACCESSIBLE_PLATE]: {
    bannerType: InlineBannerType.ATTENTION,
    text: 'You do not have access to this plate.',
  },
  [QueryBannerState.ERROR]: {
    bannerType: InlineBannerType.ERROR,
    text: 'Failed to load this plate.',
  },
};

export const TRUNCATED_BANNER_TEXT = "Some of this plate's contents aren't shown.";
export const TRUNCATED_BANNER_SUPPORTING_INFO = 'This plate has more data than can be loaded at once.';

interface ScratchPlateViewerProps {
  result: UseFilledScratchPlateDataResult;
}

export const ScratchPlateViewer: React.FC<ScratchPlateViewerProps> = ({result}) => {
  switch (result.kind) {
    case 'query': {
      const {query} = result;
      let state: QueryBannerState;
      if (query.type === QueryResultType.PENDING || query.type === QueryResultType.SKIPPED) {
        // SKIPPED occurs when query inputs are not ready yet, so this still presents as loading.
        state = QueryBannerState.LOADING;
      } else if (query.type === QueryResultType.ERROR) {
        state = QueryBannerState.ERROR;
      } else if (isInaccessible(query.data.scratchPlate)) {
        state = QueryBannerState.INACCESSIBLE_PLATE;
      } else {
        // Plate fetch succeeded but downstream data frames are still settling.
        state = QueryBannerState.LOADING;
      }
      const banner = QUERY_BANNER_CONTENT[state];
      return (
        <>
          <BannerWrapper>
            <InlineBanner
              bannerType={banner.bannerType}
              text={banner.text}
              supportingInfo={banner.supportingInfo}
            />
          </BannerWrapper>
          {state === QueryBannerState.LOADING && <LoadingSpinner />}
        </>
      );
    }
    case 'missing-content-layer':
      return (
        <BannerWrapper>
          <InlineBanner
            bannerType={InlineBannerType.WARNING}
            text="No contents found."
            supportingInfo={<>This plate appears to be empty.</>}
          />
        </BannerWrapper>
      );
    case 'hydration-error':
      return (
        <BannerWrapper>
          <InlineBanner
            bannerType={InlineBannerType.ERROR}
            text="Failed to load the contents of this plate."
          />
        </BannerWrapper>
      );
    case 'unregistered-samples':
      return (
        <BannerWrapper>
          <InlineBanner
            bannerType={InlineBannerType.ERROR}
            text="This plate contains samples that aren't linked to registered entities."
          />
        </BannerWrapper>
      );
    case 'success':
      return (
        <>
          <FilledScratchPlateHeader plateName={null} isTruncated={result.data.isTruncated} />
          <FilledScratchPlateView data={result.data} />
        </>
      );
    default:
      return assertNever(result);
  }
};

export const FilledScratchPlateHeader: React.FC<{
  plateName: string | null;
  isTruncated: boolean;
}> = ({plateName, isTruncated}) => {
  const {classes} = useStyles();
  return (
    <>
      {plateName != null && plateName !== '' && <div className={classes.plateName}>{plateName}</div>}
      {isTruncated && (
        <BannerWrapper>
          <InlineBanner
            bannerType={InlineBannerType.WARNING}
            text={TRUNCATED_BANNER_TEXT}
            supportingInfo={TRUNCATED_BANNER_SUPPORTING_INFO}
          />
        </BannerWrapper>
      )}
    </>
  );
};

const ADDITIONAL_ATTRIBUTE_OPTIONS: Array<SupportedAdditionalAttribute> = [
  REAGENTS_ATTRIBUTE_SENTINEL,
  WELL_ROLE_ATTRIBUTE_SENTINEL,
];

// Module-level constants so referential stability doesn't churn the pane on re-render.
const EMPTY_WELL_INDICES: Set<number> = new Set();
const EMPTY_REFS: Array<React.RefObject<HTMLElement>> = [];

export const FilledScratchPlateView: React.FC<{
  data: FilledScratchPlateData;
  maxPlateViewHeight?: string;
  reagentAssignmentGroupsOverride?: {[id: string]: ReagentAssignmentGroup};
  /** Rendered above the layer dropdown inside the plate-width header slot, so it aligns with the plate's left edge. */
  headerPrefix?: JSX.Element;
}> = ({data, maxPlateViewHeight = '500px', reagentAssignmentGroupsOverride, headerPrefix}) => {
  const {plateMap, containerSchemaId} = data;
  const {classes} = useStyles();

  const wellSchemaLayers = useWellSchemaLayers(containerSchemaId, noop, []);
  const wellSchemaOptions = useWellSchemaOptionsForPlateMap(plateMap, wellSchemaLayers);

  const {assignmentGroupsPerAttribute, selectedAttribute, setSelectedAttribute, displayLayer} =
    usePlateMapDisplayAttributeState(plateMap, REAGENTS_ATTRIBUTE_SENTINEL, true);

  // Filled scratch plates carry per-placement concentration but no transfer quantities, so only
  // the Concentration submenu is populated, scoped to reagents actually present on *this* plate.
  const measurementLayersOptions = useMemo(() => {
    const allReagentGroupsById =
      reagentAssignmentGroupsOverride ??
      (assignmentGroupsPerAttribute[getDisplayLayerAttributeKey(REAGENTS_ATTRIBUTE_SENTINEL)] as {
        [id: string]: ReagentAssignmentGroup;
      }) ??
      {};
    const presentGroupIds = new Set<string>();
    for (const well of plateMap.wells) {
      for (const content of well.contents) {
        presentGroupIds.add(content.groupId);
      }
    }
    const scopedReagentGroupsById = Object.fromEntries(
      Object.entries(allReagentGroupsById).filter(([id]) => presentGroupIds.has(id))
    );
    return getContentConcentrationOptions(scopedReagentGroupsById);
  }, [assignmentGroupsPerAttribute, reagentAssignmentGroupsOverride, plateMap]);

  const assignmentGroupsById =
    reagentAssignmentGroupsOverride != null && selectedAttribute === REAGENTS_ATTRIBUTE_SENTINEL
      ? reagentAssignmentGroupsOverride
      : (assignmentGroupsPerAttribute[getDisplayLayerAttributeKey(selectedAttribute)] ?? {});

  return (
    <PlateMapPane
      header={
        <>
          {headerPrefix}
          <div className={classes.layerDropdown}>
            <PlateMapDisplayLayerDropdown
              additionalAttributeOptions={ADDITIONAL_ATTRIBUTE_OPTIONS}
              selectedAttribute={selectedAttribute}
              setSelectedAttribute={setSelectedAttribute}
              plateDataParametersOptions={[]}
              wellSchemaOptions={wellSchemaOptions}
              measurementLayersOptions={measurementLayersOptions}
            />
          </div>
        </>
      }
      plateMapDisplayParams={{
        plateMap,
        displayLayer,
        attribute: selectedAttribute,
        assignmentGroupsById,
        groupedValidationErrors: {},
      }}
      isPlateMapViewOnly={true}
      selectedWellIndices={EMPTY_WELL_INDICES}
      setSelectedWellIndices={noop}
      previewedWellIndices={EMPTY_WELL_INDICES}
      deselectWellsOnClickRefs={EMPTY_REFS}
      maxPlateViewHeight={maxPlateViewHeight}
      showFinalMeasurements={true}
      plateMapType={PlateMapType.RECORD}
    />
  );
};

const useStyles = createStyles((theme) => ({
  bannerWrapper: {
    marginTop: theme.spacing.lg,
  },
  loadingSpinner: {
    marginTop: theme.spacing.md,
  },
  plateName: {
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    fontSize: theme.fontSizes.xl,
    marginBottom: theme.spacing.md,
  },
  layerDropdown: {
    // Sized to fit the longest localized label on a single line; not a theme spacing token.
    maxWidth: 264,
  },
}));

interface ScratchPlateChipModalProps extends RunModalProps<void> {
  scratchPlateId: string;
}

export function ScratchPlateChipModal({scratchPlateId}: ScratchPlateChipModalProps): JSX.Element {
  return <QueryingScratchPlateViewer scratchPlateId={scratchPlateId} />;
}

export default QueryingScratchPlateViewer;
